<template>
  <div class="vip-page">
    <!-- Header -->
    <div class="vip-header">
      <van-icon name="arrow-left" size="22" color="#fff" @click="$router.go(-1)" />
      <h1 class="vip-header__title">{{ $lang.vip_title }}</h1>
      <div class="vip-header__balance">
        <span>{{ getCurrency }} {{ $formatNumberWithCommas(balance) }}</span>
        <div class="vip-header__add" @click="$jumpTo('/rechargeCont')">
          <van-icon name="plus" size="12" color="#000" />
        </div>
      </div>
    </div>

    <!-- Level Emblem -->
    <div class="vip-emblem">
      <div class="vip-emblem__arrow" @click="prevLevel">
        <van-icon name="arrow-left" size="20" color="#fff" />
      </div>
      <div class="vip-emblem__badge">
        <img
          v-if="currentViewLevel <= currentLevel"
          :src="levelBadge"
          class="vip-emblem__img"
        />
        <div v-else class="vip-emblem__locked">
          <img :src="levelBadge" class="vip-emblem__img vip-emblem__img--locked" />
          <div class="vip-emblem__lock-overlay">
            <van-icon name="lock" size="36" color="#fff" />
          </div>
        </div>
        <p class="vip-emblem__name">{{ $lang.vip_level_text || 'Level' }} {{ currentViewLevel }}</p>
      </div>
      <div class="vip-emblem__arrow" @click="nextLevel">
        <van-icon name="arrow" size="20" color="#fff" />
      </div>
    </div>

    <!-- Progress -->
    <div class="vip-progress">
      <!-- Recharge Progress -->
      <div class="vip-progress__header">
        <span class="vip-progress__label">{{ $lang.vip_recharge_progress || 'RECHARGE PROGRESS' }}</span>
        <div class="vip-progress__xp">
          <span class="vip-progress__current">{{ $formatNumberWithCommas(rechargeAmount) }}</span>
          <span class="vip-progress__sep"> / </span>
          <span class="vip-progress__max">{{ $formatNumberWithCommas(nextRechargeAmount) }}</span>
        </div>
      </div>
      <div class="vip-progress__bar">
        <div class="vip-progress__fill" :style="{ width: rechargePercent + '%' }"></div>
      </div>

      <p class="vip-progress__remaining">
        {{ $lang.vip_next_level || 'NEXT LEVEL' }}: VIP {{ currentLevel + 1 }}
      </p>
    </div>

    <!-- Privileges -->
    <div v-if="currentViewLevel > 0" class="vip-privileges">
      <div class="vip-privileges__title">
        <span class="vip-privileges__bar"></span>
        {{ $lang.vip_exclusive_privileges }}
      </div>
      <div class="vip-privileges__grid">
        <div
          v-for="(item, idx) in privileges"
          :key="idx"
          class="vip-card"
          :class="{ 'vip-card--full': item.full, 'vip-card--locked': isLocked }"
        >
          <div v-if="isLocked" class="vip-card__lock">
            <van-icon name="lock" size="12" color="rgba(255,255,255,0.4)" />
          </div>
          <div class="vip-card__content" :class="{ 'vip-card__content--row': item.full }">
            <div class="vip-card__icon">
              <van-icon :name="item.icon" size="22" color="#24ee89" />
            </div>
            <div>
              <p class="vip-card__label">{{ item.label }}</p>
              <p class="vip-card__value" :class="{ 'vip-card__value--locked': isLocked }">
                {{ item.value }}
              </p>
            </div>
          </div>
          <!-- Claim button for claimable privileges -->
          <button
            v-if="item.claimKey && !isLocked"
            class="vip-card__claim"
            :class="{
              'vip-card__claim--active': getGiftStatus(item.claimKey) === 1,
              'vip-card__claim--done': getGiftStatus(item.claimKey) === 2
            }"
            :disabled="getGiftStatus(item.claimKey) !== 1 || getGiftClaiming(item.claimKey)"
            @click="claimByKey(item.claimKey)"
          >
            {{ getGiftClaiming(item.claimKey) ? '...' : btnText(getGiftStatus(item.claimKey)) }}
          </button>
        </div>
      </div>
    </div>

    <!-- VIP Gifts -->
    <div v-if="currentViewLevel > 0" class="vip-gifts">
      <div class="vip-gifts__title">
        <span class="vip-gifts__bar"></span>
        {{ $lang.vip_my_gifts || 'MY GIFTS' }}
      </div>
      <div class="vip-gifts__list">
        <!-- Upgrade Bonus -->
        <div class="vip-gift-item">
          <div class="vip-gift-item__icon">
            <van-icon name="gift-o" size="22" color="#24ee89" />
          </div>
          <div class="vip-gift-item__body">
            <p class="vip-gift-item__name">{{ $lang.vip_level_up_reward }}</p>
            <p class="vip-gift-item__amount" v-if="(currentViewConfig.upgradeBonus || 0) > 0">{{ getCurrency }} {{ $formatNumberWithCommas(currentViewConfig.upgradeBonus) }}</p>
            <p class="vip-gift-item__status" v-else>{{ statusText(upgradeStatus) }}</p>
          </div>
          <button
            class="vip-gift-item__btn"
            :class="{
              'vip-gift-item__btn--claim': upgradeStatus === 1,
              'vip-gift-item__btn--done': upgradeStatus === 2,
              'vip-gift-item__btn--locked': upgradeStatus === 0
            }"
            :disabled="upgradeStatus !== 1 || claimingUpgrade"
            @click="claimUpgrade(currentViewLevel)"
          >
            {{ claimingUpgrade ? '...' : btnText(upgradeStatus) }}
          </button>
        </div>

        <!-- Daily Reward -->
        <div class="vip-gift-item">
          <div class="vip-gift-item__icon">
            <van-icon name="calendar-o" size="22" color="#24ee89" />
          </div>
          <div class="vip-gift-item__body">
            <p class="vip-gift-item__name">{{ $lang.vip_daily_reward }}</p>
            <p class="vip-gift-item__amount" v-if="signViewReward > 0">{{ getCurrency }} {{ $formatNumberWithCommas(signViewReward) }}</p>
            <p class="vip-gift-item__status" v-else>{{ statusText(signStatus) }}</p>
          </div>
          <button
            class="vip-gift-item__btn"
            :class="{
              'vip-gift-item__btn--claim': signStatus === 1,
              'vip-gift-item__btn--done': signStatus === 2,
              'vip-gift-item__btn--locked': signStatus === 0
            }"
            :disabled="signStatus !== 1 || claimingSign"
            @click="claimSign"
          >
            {{ claimingSign ? '...' : btnText(signStatus) }}
          </button>
        </div>

        <!-- Birthday Gift -->
        <div class="vip-gift-item">
          <div class="vip-gift-item__icon">
            <van-icon name="star-o" size="22" color="#24ee89" />
          </div>
          <div class="vip-gift-item__body">
            <p class="vip-gift-item__name">{{ $lang.vip_birthday_gift }}</p>
            <p class="vip-gift-item__amount" v-if="(currentViewConfig.monthlyBonus || 0) > 0">{{ getCurrency }} {{ $formatNumberWithCommas(currentViewConfig.monthlyBonus) }}</p>
            <p class="vip-gift-item__status" v-else>{{ statusText(birthdayStatus) }}</p>
          </div>
          <button
            class="vip-gift-item__btn"
            :class="{
              'vip-gift-item__btn--claim': birthdayStatus === 1,
              'vip-gift-item__btn--done': birthdayStatus === 2,
              'vip-gift-item__btn--locked': birthdayStatus === 0
            }"
            :disabled="birthdayStatus !== 1 || claimingBirthday"
            @click="claimBirthday"
          >
            {{ claimingBirthday ? '...' : btnText(birthdayStatus) }}
          </button>
        </div>

        <!-- Loss Rebate -->
        <div class="vip-gift-item">
          <div class="vip-gift-item__icon">
            <van-icon name="exchange" size="22" color="#24ee89" />
          </div>
          <div class="vip-gift-item__body">
            <p class="vip-gift-item__name">{{ $lang.vip_loss_rebate }}</p>
            <p class="vip-gift-item__amount" v-if="(currentViewConfig.signBalLimit || 0) > 0">{{ getCurrency }} {{ $formatNumberWithCommas(currentViewConfig.signBalLimit) }}</p>
            <p class="vip-gift-item__status" v-else>{{ statusText(cashBackStatus) }}</p>
          </div>
          <button
            class="vip-gift-item__btn"
            :class="{
              'vip-gift-item__btn--claim': cashBackStatus === 1,
              'vip-gift-item__btn--done': cashBackStatus === 2,
              'vip-gift-item__btn--locked': cashBackStatus === 0
            }"
            :disabled="cashBackStatus !== 1 || claimingCashBack"
            @click="claimCashBack"
          >
            {{ claimingCashBack ? '...' : btnText(cashBackStatus) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VipInit, GetVipAwardInit, GetVipAward, GetSignIn, SignIn, BirthdayAwardInit, ReceiveBirthdayAward, CashBackInit, GetCashBack, GameBalanceList } from '@/api/common'

export default {
  name: 'VipLevels',
  data() {
    return {
      balance: 0,
      currentLevel: 0,
      currentViewLevel: 0,
      vipList: [],
      betAmount: 0,
      rechargeAmount: 0,
      nextBetAmount: 0,
      nextRechargeAmount: 0,
      // Gift claim states
      upgradeRuleList: [],
      claimingUpgrade: false,
      signStatus: 0,
      signAward: 0,
      signRuleList: [],
      claimingSign: false,
      birthdayStatus: 0,
      birthdayAward: 0,
      claimingBirthday: false,
      cashBackStatus: 0,
      cashBackAmount: 0,
      claimingCashBack: false
    }
  },
  computed: {
    totalLevels() {
      return this.vipList.length > 0 ? this.vipList.length - 1 : 0
    },
    rechargePercent() {
      return this.nextRechargeAmount > 0
        ? Math.min((this.rechargeAmount / this.nextRechargeAmount) * 100, 100)
        : 100
    },
    betPercent() {
      return this.nextBetAmount > 0
        ? Math.min((this.betAmount / this.nextBetAmount) * 100, 100)
        : 100
    },
    upgradeStatus() {
      if (!Array.isArray(this.upgradeRuleList)) return 0
      const rule = this.upgradeRuleList.find(r => r.vipLevel === this.currentViewLevel)
      return rule ? rule.receiveStatus : 0
    },
    upgradeAward() {
      if (!Array.isArray(this.upgradeRuleList)) return 0
      const rule = this.upgradeRuleList.find(r => r.vipLevel === this.currentViewLevel)
      return rule ? rule.reward : 0
    },
    isLocked() {
      return this.currentViewLevel > this.currentLevel
    },
    levelBadge() {
      try {
        return require(`@/assets/img/vip/${this.currentViewLevel}.png`)
      } catch (e) {
        return ''
      }
    },
    currentViewConfig() {
      return this.vipList.find(v => v.vipLevel === this.currentViewLevel) || {}
    },
    signViewReward() {
      const effectiveLevel = Math.max(this.currentViewLevel, this.currentLevel)
      if (effectiveLevel === this.currentLevel && this.signAward > 0) {
        return this.signAward
      }
      const rule = this.signRuleList.find(r => r.vipLevel === effectiveLevel)
      return rule ? rule.reward : 0
    },
    privileges() {
      const c = this.getCurrency
      const cfg = this.currentViewConfig
      return [
        { icon: 'gift-o', label: this.$lang.vip_level_up_reward, value: c + ' ' + this.$formatNumberWithCommas(cfg.upgradeBonus || 0), claimKey: 'upgrade' },
        { icon: 'calendar-o', label: this.$lang.vip_daily_reward, value: c + ' ' + this.$formatNumberWithCommas(this.signViewReward || 0), claimKey: 'sign' },
        { icon: 'star-o', label: this.$lang.vip_birthday_gift, value: c + ' ' + this.$formatNumberWithCommas(cfg.monthlyBonus || 0), claimKey: 'birthday' },
        { icon: 'balance-o', label: this.$lang.vip_withdrawal_fee, value: (cfg.withdrawFee != null ? cfg.withdrawFee + '%' : '--') },
        { icon: 'add-o', label: this.$lang.vip_daily_withdrawal, value: (cfg.withdrawCount || 0) + '' },
        { icon: 'cash-back-record', label: this.$lang.vip_withdrawal_limit, value: c + ' ' + this.$formatNumberWithCommas(cfg.withdrawLimit || 0) },
        { icon: 'exchange', label: this.$lang.vip_loss_rebate, value: c + ' ' + this.$formatNumberWithCommas(cfg.signBalLimit || 0), full: true, claimKey: 'cashBack' }
      ]
    }
  },
  mounted() {
    if (this.token) {
      this.loadBalance()
      this.loadVipInit()
      this.loadGiftStatuses()
    }
  },
  methods: {
    statusText(s) {
      if (s === 0) return this.$lang.vip_not_reached || 'Not reached'
      if (s === 1) return this.$lang.vip_claimable || 'Claimable'
      if (s === 2) return this.$lang.vip_claimed || 'Claimed'
      return '--'
    },
    btnText(s) {
      if (s === 0) return this.$lang.vip_not_reached || 'Locked'
      if (s === 1) return this.$lang.ng_claim || 'Claim'
      if (s === 2) return this.$lang.vip_claimed || 'Claimed'
      return '--'
    },
    getGiftStatus(key) {
      const map = { upgrade: this.upgradeStatus, sign: this.signStatus, birthday: this.birthdayStatus, cashBack: this.cashBackStatus }
      return map[key] || 0
    },
    getGiftClaiming(key) {
      const map = { upgrade: this.claimingUpgrade, sign: this.claimingSign, birthday: this.claimingBirthday, cashBack: this.claimingCashBack }
      return map[key] || false
    },
    claimByKey(key) {
      const map = { upgrade: this.claimUpgrade, sign: this.claimSign, birthday: this.claimBirthday, cashBack: this.claimCashBack }
      if (map[key]) map[key](this.currentViewLevel)
    },
    async loadBalance() {
      try {
        const data = await GameBalanceList()
        if (data && data.status === 'ok' && data.content) {
          const c = data.content
          this.balance = c.totalBalance || c.balance || 0
        }
      } catch (e) {
        console.error('GameBalanceList error', e)
      }
    },
    async loadVipInit() {
      try {
        const data = await VipInit()
        if (data.status === 'ok') {
          const c = data.content
          this.currentLevel = c.vipLevel || 0
          this.currentViewLevel = c.vipLevel || 0
          this.vipList = c.vipList || []
          this.betAmount = c.betAmount || 0
          this.rechargeAmount = c.rechargeAmount || 0
          this.nextBetAmount = c.nextBetAmount || 0
          this.nextRechargeAmount = c.nextRechargeAmount || 0
        }
      } catch (e) {
        console.error('loadVipInit error', e)
      }
    },
    prevLevel() {
      if (this.currentViewLevel > 0) this.currentViewLevel--
    },
    nextLevel() {
      if (this.currentViewLevel < this.totalLevels) this.currentViewLevel++
    },
    async loadGiftStatuses() {
      try {
        const [upRes, signRes, bdRes, cbRes] = await Promise.all([
          GetVipAwardInit({}),
          GetSignIn({}),
          BirthdayAwardInit({}),
          CashBackInit({})
        ])
        if (upRes && upRes.status === 'ok' && upRes.content) {
          let rl = upRes.content.ruleList
          if (typeof rl === 'string') {
            try { rl = JSON.parse(rl) } catch (e) { rl = [] }
          }
          this.upgradeRuleList = Array.isArray(rl) ? rl : []
        }
        if (signRes && signRes.status === 'ok' && signRes.content) {
          this.signStatus = signRes.content.receiveStatus || 0
          this.signAward = signRes.content.awardAmount || 0
          let srl = signRes.content.ruleList
          if (typeof srl === 'string') {
            try { srl = JSON.parse(srl) } catch (e) { srl = [] }
          }
          this.signRuleList = Array.isArray(srl) ? srl : []
        }
        if (bdRes && bdRes.status === 'ok' && bdRes.content) {
          this.birthdayStatus = bdRes.content.receiveStatus || 0
          this.birthdayAward = bdRes.content.awardAmount || 0
        }
        if (cbRes && cbRes.status === 'ok' && cbRes.content) {
          this.cashBackStatus = cbRes.content.receiveStatus || 0
          this.cashBackAmount = cbRes.content.backAmount || 0
        }
      } catch (e) {
        console.error('loadGiftStatuses error', e)
      }
    },
    async claimUpgrade(targetLevel) {
      if (this.claimingUpgrade) return
      this.claimingUpgrade = true
      try {
        const level = targetLevel != null ? targetLevel : this.currentViewLevel
        const res = await GetVipAward({ targetVipLevel: level })
        if (res && res.status === 'ok' && res.content) {
          // Update the ruleList entry for this level
          const rule = this.upgradeRuleList.find(r => r.vipLevel === level)
          if (rule) {
            this.$set(rule, 'receiveStatus', 2)
          }
          const amount = res.content.awardAmount || this.upgradeAward || 0
          this.$toast({ message: '+' + this.getCurrency + this.$formatNumberWithCommas(amount), icon: 'success' })
        } else {
          this.$toast(res.msg || 'Error')
        }
      } catch (e) {
        this.$toast(e.msg || 'Error')
      } finally {
        this.claimingUpgrade = false
      }
    },
    async claimSign() {
      if (this.claimingSign) return
      this.claimingSign = true
      try {
        const res = await SignIn({})
        if (res && res.status === 'ok' && res.content) {
          const amount = res.content.awardAmount || this.signAward || 0
          this.signStatus = 2
          this.signAward = amount
          this.$toast({ message: '+' + this.getCurrency + this.$formatNumberWithCommas(amount), icon: 'success' })
        } else {
          this.$toast(res.msg || 'Error')
        }
      } catch (e) {
        this.$toast(e.msg || 'Error')
      } finally {
        this.claimingSign = false
      }
    },
    async claimBirthday() {
      if (this.claimingBirthday) return
      this.claimingBirthday = true
      try {
        const res = await ReceiveBirthdayAward({})
        if (res && res.status === 'ok' && res.content) {
          this.birthdayStatus = res.content.receiveStatus || 2
          const amount = res.content.awardAmount || this.birthdayAward || 0
          this.$toast({ message: '+' + this.getCurrency + this.$formatNumberWithCommas(amount), icon: 'success' })
        } else {
          this.$toast(res.msg || 'Error')
        }
      } catch (e) {
        this.$toast(e.msg || 'Error')
      } finally {
        this.claimingBirthday = false
      }
    },
    async claimCashBack() {
      if (this.claimingCashBack) return
      this.claimingCashBack = true
      try {
        const res = await GetCashBack({})
        if (res && res.status === 'ok' && res.content) {
          this.cashBackStatus = res.content.receiveStatus || 2
          const amount = res.content.backAmount || this.cashBackAmount || 0
          this.$toast({ message: '+' + this.getCurrency + this.$formatNumberWithCommas(amount), icon: 'success' })
        } else {
          this.$toast(res.msg || 'Error')
        }
      } catch (e) {
        this.$toast(e.msg || 'Error')
      } finally {
        this.claimingCashBack = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.vip-page {
  min-height: 100vh;
  background: #1a1d29;
  color: #e3e4f5;
  padding-bottom: 40px;
}

.vip-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: #1a1d29;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  height: 50px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  justify-content: space-between;

  &__title {
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #fff;
  }

  &__balance {
    background: #212536;
    border-radius: 20px;
    padding: 4px 4px 4px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    border: 1px solid rgba(100, 116, 139, 0.3);
  }

  &__add {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #24ee89;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.vip-emblem {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  gap: 8px;

  &__arrow {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #1b1f2e;
    border-radius: 4px;
  }

  &__badge {
    text-align: center;
    width: 140px;
    height: 140px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__img {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }

  &__img--locked {
    filter: grayscale(0.5) brightness(0.75);
  }

  &__locked {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__lock-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
  }

  &__name {
    margin-top: 4px;
    font-size: 14px;
    font-weight: 700;
    color: #24ee89;
    text-transform: uppercase;
  }
}

.vip-progress {
  margin: 0 16px 24px;
  background: #111420;
  padding: 20px;
  border-left: 4px solid #24ee89;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 4px;
  }

  &__label {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #a8aaba;
  }

  &__current {
    font-size: 20px;
    font-weight: 700;
    color: #24ee89;
  }

  &__sep {
    color: #a8aaba;
    margin: 0 2px;
  }

  &__max {
    font-size: 12px;
    color: #a8aaba;
  }

  &__sub {
    font-size: 18px;
    font-weight: 700;
    color: #e3e4f5;
    margin-bottom: 12px;
  }

  &__bar {
    width: 100%;
    height: 28px;
    background: #212536;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.5);
    position: relative;
  }

  &__fill {
    height: 100%;
    background: linear-gradient(90deg, #24ee89, #20ec87);
    border-radius: 14px;
    box-shadow: 0 0 15px rgba(36, 238, 137, 0.4);
    transition: width 0.5s ease;

    &--gold {
      background: linear-gradient(90deg, #f8f510, #ffd700);
      box-shadow: 0 0 15px rgba(248, 245, 16, 0.4);
    }
  }

  &__remaining {
    margin-top: 12px;
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #a8aaba;
  }
}

.vip-privileges {
  padding: 0 16px;

  &__title {
    font-size: 16px;
    font-weight: 800;
    color: #e3e4f5;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__bar {
    width: 4px;
    height: 20px;
    background: #f8f510;
    display: inline-block;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
}

.vip-card {
  position: relative;
  background: #1b1f2e;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid rgba(69, 71, 85, 0.3);

  &--full {
    grid-column: span 2;
  }

  &--locked .vip-card__content {
    opacity: 0.5;
    filter: grayscale(0.5);
  }

  &__lock {
    position: absolute;
    top: 8px;
    right: 8px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &--row {
      flex-direction: row;
      align-items: center;
    }
  }

  &__icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #212536;
    border-radius: 4px;
  }

  &__label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #a8aaba;
    margin-bottom: 2px;
  }

  &__value {
    font-size: 15px;
    font-weight: 700;
    color: #f8f510;

    &--locked {
      color: #727583;
    }
  }

  &__claim {
    margin-top: 8px;
    width: 100%;
    font-size: 10px;
    font-weight: 800;
    padding: 6px 0;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: rgba(255, 255, 255, 0.05);
    color: #4b5563;

    &--active {
      background: #24ee89;
      color: #000;
      box-shadow: 0 0 8px rgba(36, 238, 137, 0.4);
    }

    &--done {
      background: rgba(255, 255, 255, 0.05);
      color: #727583;
      cursor: not-allowed;
    }

    &:disabled {
      cursor: not-allowed;
    }

    &--active:active {
      transform: scale(0.95);
    }
  }
}

@media (min-width: 769px) {
  .vip-page {
    max-width: 450px;
    margin: 0 auto;
  }
}

// ====== VIP GIFTS ======
.vip-gifts {
  padding: 0 16px;
  margin-top: 24px;

  &__title {
    font-size: 16px;
    font-weight: 800;
    color: #e3e4f5;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__bar {
    width: 4px;
    height: 20px;
    background: #24ee89;
    display: inline-block;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}

.vip-gift-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #1b1f2e;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid rgba(69, 71, 85, 0.3);

  &__icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #212536;
    border-radius: 8px;
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #e3e4f5;
  }

  &__amount {
    font-size: 15px;
    font-weight: 700;
    color: #f8f510;
    margin-top: 2px;
  }

  &__status {
    font-size: 11px;
    color: #727583;
    margin-top: 2px;
  }

  &__btn {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 900;
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &--claim {
      background: #24ee89;
      color: #000;
      box-shadow: 0 0 10px rgba(36, 238, 137, 0.4);
    }

    &--done {
      background: rgba(255, 255, 255, 0.05);
      color: #727583;
      cursor: not-allowed;
    }

    &--locked {
      background: rgba(255, 255, 255, 0.05);
      color: #4b5563;
      cursor: not-allowed;
    }

    &:disabled {
      cursor: not-allowed;
    }

    &--claim:active {
      transform: scale(0.95);
    }
  }
}
</style>
